// MARK://dhk8/admin-shell/lib/clients/reports
// Visor de los read-models del negocio → dhk8-erp-reports (GraphQL, F2.6 / ADR-0009).
//   query reportSales(from,to)       → ventas diarias + totales
//   query reportTopProducts(...)     → top productos
//   query reportTopServers(...)      → top meseros
//   query reportTips(...)            → propinas
//   query reportCuts(...)            → cortes de caja Z/X
// Todas tenant-scoped por el JWT (RLS). Los read-models los alimenta la SAGA de
// eventos POS; si las proyecciones están vacías, el visor muestra estado vacío.
// Reportes son read-only — no hay escrituras.
//
// NOTA: `covers` (comensales) no lo exponen los read-models todavía → 0.
import { PREVIEW_STUB, mock } from '../env.js'
import { requireTenant } from '../api.js'
import { gqlClient } from '../gql.js'
import {
  AdminReportSummaryQuery,
  AdminReportPrevSalesQuery,
  AdminReportSalesQuery,
  AdminReportTopProductsQuery,
  AdminReportTopServersQuery,
  AdminReportCutsQuery,
  RebuildReportProjectionsMutation,
} from '../queries/reports.queries.js'
import * as M from '../mock-data.js'

export interface ReportDetail {
  id: string
  name: string
  period: string
  columns: string[]
  rows: (string | number)[][]
  totals?: (string | number)[]
}

/** Un tender (método de pago) con su monto + conteo en el periodo. */
export interface PaymentMethodEntry {
  method: string
  count: number
  amountCents: number
}

/** Dashboard KPIs for `/reports` landing page. */
export interface ReportSummary {
  /** Total sales in MXN cents for the period. */
  salesCents: number
  /** Total ticket count. */
  tickets: number
  /** Average ticket size in MXN cents. */
  avgTicketCents: number
  /** Total tips in MXN cents. */
  tipsCents: number
  /** Total covers (comensales). */
  covers: number
  /** Period label, e.g. "Hoy". */
  period: string
  // ── Ola 2 ────────────────────────────────────────────────────────────────
  /** IVA cobrado en el periodo (MXN cents). */
  taxCents: number
  /** Desglose de métodos de pago (CAPTURED) del periodo. */
  paymentMethods: PaymentMethodEntry[]
  /** # de órdenes canceladas en el periodo. */
  cancelledCount: number
  /** Monto de las órdenes canceladas (MXN cents). */
  cancelledCents: number
  /** # de órdenes CLOSED con descuento. */
  discountedOrders: number
  /** Total de descuentos aplicados (MXN cents). */
  discountCents: number
  /** Comparativo: ventas netas del periodo ANTERIOR equivalente (MXN cents). */
  prevSalesCents: number
  /** Comparativo: # tickets del periodo ANTERIOR equivalente. */
  prevTickets: number
}

export interface DateRange {
  /** ISO date, e.g. "2026-05-15" — inclusive. */
  from: string
  /** ISO date — inclusive. */
  to: string
}

/** ISO `YYYY-MM-DD` for today (local) — used as the default range. */
export function today(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const mxn = (cents: number): number => Number((cents / 100).toFixed(2))
const rangeLabel = (r?: DateRange): string => (r ? `${r.from} → ${r.to}` : 'Periodo')

// Catálogo de reportes disponibles (índice de navegación del visor). No es un dato
// del backend: enumera qué read-models puede consultar el Admin.
const REPORTS_CATALOG: M.ReportSummary[] = [
  { id: 'rep-ventas-dia', name: 'Ventas del día', period: 'Hoy', updatedAt: new Date().toISOString() },
  { id: 'rep-ventas-mes', name: 'Ventas del mes', period: 'Mes en curso', updatedAt: new Date().toISOString() },
  { id: 'rep-productos', name: 'Productos más vendidos', period: 'Periodo', updatedAt: new Date().toISOString() },
  { id: 'rep-meseros', name: 'Desempeño por mesero', period: 'Periodo', updatedAt: new Date().toISOString() },
  { id: 'rep-cortes', name: 'Cortes de caja', period: 'Periodo', updatedAt: new Date().toISOString() },
]

export async function listReports(_signal?: AbortSignal): Promise<M.ReportSummary[]> {
  if (PREVIEW_STUB) return mock(M.REPORTS)
  return REPORTS_CATALOG
}

const EMPTY_SUMMARY: ReportSummary = {
  salesCents: 0, tickets: 0, avgTicketCents: 0, tipsCents: 0, covers: 0, period: 'Hoy',
  taxCents: 0, paymentMethods: [], cancelledCount: 0, cancelledCents: 0, discountedOrders: 0, discountCents: 0,
  prevSalesCents: 0, prevTickets: 0,
}

const PREVIEW_SUMMARY: ReportSummary = {
  salesCents: 2913000, tickets: 82, avgTicketCents: 35524, tipsCents: 41530, covers: 194, period: 'Hoy',
  taxCents: 401793, cancelledCount: 3, cancelledCents: 48200, discountedOrders: 11, discountCents: 36400,
  paymentMethods: [
    { method: 'CASH', count: 41, amountCents: 1452000 },
    { method: 'CARD', count: 36, amountCents: 1338000 },
    { method: 'TRANSFER', count: 5, amountCents: 123000 },
  ],
  prevSalesCents: 2640000, prevTickets: 76,
}

/** Periodo ANTERIOR equivalente (misma duración, justo antes de `r.from`). */
function prevRange(r: DateRange): DateRange {
  const from = new Date(`${r.from}T00:00:00.000Z`)
  const to = new Date(`${r.to}T00:00:00.000Z`)
  const dayMs = 86_400_000
  const days = Math.max(1, Math.round((to.getTime() - from.getTime()) / dayMs) + 1)
  const prevTo = new Date(from.getTime() - dayMs)
  const prevFrom = new Date(prevTo.getTime() - (days - 1) * dayMs)
  const ymd = (d: Date): string => d.toISOString().slice(0, 10)
  return { from: ymd(prevFrom), to: ymd(prevTo) }
}

export async function getReportSummary(range?: DateRange, signal?: AbortSignal): Promise<ReportSummary> {
  if (PREVIEW_STUB) return mock(PREVIEW_SUMMARY)
  if (requireTenant() === null) return EMPTY_SUMMARY
  // Sin rango → histórico (sin filtro de fecha), igual que el dashboard consumía
  // antes. La ReportsPage siempre pasa un rango explícito (date pickers).
  const from = range?.from
  const to = range?.to
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  // Comparativo: si hay rango, traemos en paralelo los totales del periodo anterior.
  const prev = range ? prevRange(range) : undefined
  const [res, prevRes] = await Promise.all([
    gqlClient.query(AdminReportSummaryQuery, { from, to }, ctx).toPromise(),
    prev
      ? gqlClient.query(AdminReportPrevSalesQuery, { from: prev.from, to: prev.to }, ctx).toPromise()
      : Promise.resolve(null),
  ])
  if (res.error) throw res.error
  const st = res.data?.reportSales.totals
  const tips = res.data?.reportTips.totals
  const tax = res.data?.reportTaxes
  const vd = res.data?.reportVoidsDiscounts
  const pm = res.data?.reportPaymentMethods ?? []
  const prevSt = prevRes && !prevRes.error ? prevRes.data?.reportSales.totals : undefined
  return {
    salesCents: st?.netCents ?? 0,
    tickets: st?.ordersCount ?? 0,
    avgTicketCents: st?.avgTicketCents ?? 0,
    tipsCents: tips?.totalCents ?? 0,
    covers: 0, // los read-models no exponen comensales todavía
    period: range ? rangeLabel(range) : 'Hoy',
    taxCents: tax?.taxCents ?? 0,
    paymentMethods: pm.map((p) => ({ method: p.method, count: p.count, amountCents: p.amountCents })),
    cancelledCount: vd?.cancelledCount ?? 0,
    cancelledCents: vd?.cancelledCents ?? 0,
    discountedOrders: vd?.discountedOrders ?? 0,
    discountCents: vd?.discountCents ?? 0,
    prevSalesCents: prevSt?.netCents ?? 0,
    prevTickets: prevSt?.ordersCount ?? 0,
  }
}

/** Resultado de recalcular (rebuild) las proyecciones de reports. */
export interface RebuildReportsResult {
  ordersReprocessed: number
  tablesAffected: string[]
  from: string | null
  to: string | null
}

/**
 * Recalcula (rebuild) los read-models de reports desde la fuente (PosOrder CLOSED)
 * para el rango dado. Acción pesada (admin/owner) — el backend la gatea por
 * `erp-reports:report:rebuild@*`. IDEMPOTENTE: correrla N veces da el mismo
 * resultado (clear-range + re-proyección). Lanza si el grafo devuelve error
 * (p.ej. FORBIDDEN si el usuario no tiene el permiso).
 */
export async function rebuildReportProjections(range?: DateRange, signal?: AbortSignal): Promise<RebuildReportsResult> {
  const from = range?.from
  const to = range?.to
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.mutation(RebuildReportProjectionsMutation, { from, to }, ctx).toPromise()
  if (res.error) throw res.error
  const r = res.data?.rebuildReportProjections
  return {
    ordersReprocessed: r?.ordersReprocessed ?? 0,
    tablesAffected: r?.tablesAffected ?? [],
    from: r?.from ?? null,
    to: r?.to ?? null,
  }
}

const PREVIEW_DETAILS: Record<string, ReportDetail> = {
  'rep-ventas-dia': {
    id: 'rep-ventas-dia', name: 'Ventas del día', period: 'Hoy',
    columns: ['Hora', 'Tickets', 'Comensales', 'Ventas (MXN)'],
    rows: [['12:00–14:00', 18, 41, 6420], ['14:00–16:00', 24, 58, 8930], ['16:00–18:00', 9, 19, 2540], ['18:00–22:00', 31, 76, 11240]],
    totals: ['Total', 82, 194, 29130],
  },
  'rep-productos': {
    id: 'rep-productos', name: 'Productos más vendidos', period: 'Últimos 30 días',
    columns: ['Producto', 'Unidades', 'Ventas (MXN)'],
    rows: [['Tacos al pastor (3)', 412, 49440], ['Pollo en mole', 198, 32670], ['Guacamole', 305, 28975], ['Agua de jamaica', 520, 23400], ['Flan napolitano', 142, 9230]],
  },
  'rep-meseros': {
    id: 'rep-meseros', name: 'Desempeño por mesero', period: 'Últimos 30 días',
    columns: ['Mesero', 'Tickets', 'Ticket prom. (MXN)', 'Propinas (MXN)'],
    rows: [['Beto Mesero', 320, 348, 12480], ['Dora Mesera', 285, 372, 11920], ['Ana Gerente', 96, 410, 4210]],
  },
  'rep-cortes': {
    id: 'rep-cortes', name: 'Cortes de caja', period: 'Últimos 30 días',
    columns: ['Fecha', 'Cajero', 'Efectivo (MXN)', 'Tarjeta (MXN)', 'Diferencia'],
    rows: [['2026-05-09', 'Ana Gerente', 8420, 14230, 0], ['2026-05-10', 'Ana Gerente', 9120, 15880, -25], ['2026-05-11', 'Beto Mesero', 3210, 6440, 0]],
  },
  'rep-ventas-mes': {
    id: 'rep-ventas-mes', name: 'Ventas del mes', period: 'Mes en curso',
    columns: ['Día', 'Tickets', 'Ventas (MXN)'],
    rows: Array.from({ length: 11 }, (_, i) => [`2026-05-${String(i + 1).padStart(2, '0')}`, 60 + ((i * 7) % 30), 18000 + ((i * 1234) % 9000)]),
  },
}

export async function getReport(id: string, range?: DateRange, signal?: AbortSignal): Promise<ReportDetail> {
  if (PREVIEW_STUB) {
    const d = PREVIEW_DETAILS[id]
    if (d) return mock(d)
    return mock({ id, name: id, period: '—', columns: ['(sin datos)'], rows: [] })
  }
  if (requireTenant() === null) return { id, name: id, period: '—', columns: ['(sin datos)'], rows: [] }
  const from = range?.from
  const to = range?.to
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const period = rangeLabel(range)

  switch (id) {
    case 'rep-ventas-dia':
    case 'rep-ventas-mes': {
      const res = await gqlClient.query(AdminReportSalesQuery, { from, to }, ctx).toPromise()
      if (res.error) throw res.error
      const d = res.data?.reportSales
      const t = d?.totals
      return {
        id, name: id === 'rep-ventas-mes' ? 'Ventas del mes' : 'Ventas del día', period,
        columns: ['Fecha', 'Órdenes', 'Bruto (MXN)', 'Neto (MXN)', 'Propinas (MXN)'],
        rows: (d?.rows ?? []).map((r) => [r.businessDate.slice(0, 10), r.ordersCount, mxn(r.grossCents), mxn(r.netCents), mxn(r.tipsCents)]),
        ...(t ? { totals: ['Total', t.ordersCount, mxn(t.grossCents), mxn(t.netCents), mxn(t.tipsCents)] } : {}),
      }
    }
    case 'rep-productos': {
      const res = await gqlClient.query(AdminReportTopProductsQuery, { from, to, limit: 50 }, ctx).toPromise()
      if (res.error) throw res.error
      return {
        id, name: 'Productos más vendidos', period,
        columns: ['Producto', 'Unidades', 'Ingreso (MXN)'],
        rows: (res.data?.reportTopProducts ?? []).map((p) => [p.productName, p.qty, mxn(p.grossCents)]),
      }
    }
    case 'rep-meseros': {
      const res = await gqlClient.query(AdminReportTopServersQuery, { from, to, limit: 50 }, ctx).toPromise()
      if (res.error) throw res.error
      return {
        id, name: 'Desempeño por mesero', period,
        columns: ['Mesero', 'Órdenes', 'Ingreso (MXN)', 'Propinas (MXN)'],
        rows: (res.data?.reportTopServers ?? []).map((s) => [s.serverName, s.ordersCount, mxn(s.grossCents), mxn(s.tipsCents)]),
      }
    }
    case 'rep-cortes': {
      const res = await gqlClient.query(AdminReportCutsQuery, { from, to, limit: 100 }, ctx).toPromise()
      if (res.error) throw res.error
      return {
        id, name: 'Cortes de caja', period,
        columns: ['Fecha', 'Caja', 'Tipo', 'Esperado (MXN)', 'Contado (MXN)', 'Diferencia (MXN)'],
        rows: (res.data?.reportCuts ?? []).map((c) => [c.businessDate.slice(0, 10), c.branchId, c.kind, mxn(c.expectedCents), mxn(c.countedCents), mxn(c.varianceCents)]),
      }
    }
    default:
      return { id, name: id, period: '—', columns: ['(sin datos)'], rows: [] }
  }
}
